/**
 * PSLF Debug Scenarios — granular overrides for testing every visual state
 * of HeroDashboard and PslfRecommendations without needing specific real users.
 *
 * Stored in URL params + localStorage so debug URLs can be shared with the team.
 * Activated by `?pslf_debug=true` in the URL.
 */
import type { LoanSummary, SummaryByLoanType } from 'tio-common/types/loan-summary';
import type { LivePslfData, LivePslfForm } from 'tio-employee/services/reimagined-data';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type FormScenario =
  | 'live-data'
  | 'blank-slate'
  | 'borrower-signed'
  | 'employer-signed'
  | 'submitted'
  | 'multi-form';

export const FORM_SCENARIOS: { value: FormScenario; label: string; description: string }[] = [
  { value: 'live-data', label: 'Live Data', description: 'Use real employee data' },
  { value: 'blank-slate', label: 'Blank Slate', description: 'New user, no forms' },
  {
    value: 'borrower-signed',
    label: 'Borrower Signed',
    description: 'Awaiting employer signature',
  },
  { value: 'employer-signed', label: 'Employer Signed', description: 'Ready to submit to DOE' },
  { value: 'submitted', label: 'Submitted', description: 'Fully signed and faxed' },
  { value: 'multi-form', label: 'Multiple Forms', description: 'Current + prior employers' },
];

export interface DebugLoan {
  name: string;
  loanType: 'direct' | 'ffel';
  currentBalance: number;
  qualifyingPayments: number;
}

export interface PslfDebugState {
  enabled: boolean;
  formScenario: FormScenario;
  annualReminder: boolean;
  hasNsldsData: boolean;
  debugLoans: DebugLoan[];
  estimatorCompleted: boolean;
  showRecommendations: boolean;
  consolidateLoans: boolean;
  missingRepaymentPlan: boolean;
  changeRepaymentPlan: boolean;
  linkLoans: boolean;
  recertifyIncome: boolean;
}

const STORAGE_KEY = 'pslf-debug-state';
const URL_PARAM = 'pslf_debug';

// ---------------------------------------------------------------------------
// Default state
// ---------------------------------------------------------------------------

export const DEFAULT_DEBUG_LOAN: DebugLoan = {
  name: 'Direct Loan',
  loanType: 'direct',
  currentBalance: 45000,
  qualifyingPayments: 45,
};

export function getDefaultDebugState(): PslfDebugState {
  return {
    enabled: false,
    formScenario: 'live-data',
    annualReminder: false,
    hasNsldsData: false,
    debugLoans: [{ ...DEFAULT_DEBUG_LOAN }],
    estimatorCompleted: false,
    showRecommendations: false,
    consolidateLoans: false,
    missingRepaymentPlan: false,
    changeRepaymentPlan: false,
    linkLoans: false,
    recertifyIncome: false,
  };
}

// ---------------------------------------------------------------------------
// Read / write
// ---------------------------------------------------------------------------

export function isPslfDebugActive(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get(URL_PARAM) === 'true') return true;
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      return (JSON.parse(stored) as PslfDebugState).enabled;
    } catch {
      return false;
    }
  }
  return false;
}

export function getPslfDebugState(): PslfDebugState {
  if (typeof window === 'undefined') return getDefaultDebugState();

  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      const parsed = JSON.parse(stored) as Partial<PslfDebugState>;
      return { ...getDefaultDebugState(), ...parsed };
    } catch {
      // corrupted, reset
    }
  }
  return getDefaultDebugState();
}

export function setPslfDebugState(state: PslfDebugState): void {
  if (typeof window === 'undefined') return;

  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));

  // Sync form scenario to the legacy demo key so fetchPslfData() picks it up
  if (state.formScenario !== 'live-data') {
    localStorage.setItem('reimagined-demo-pslf-scenario', state.formScenario);
  } else {
    localStorage.removeItem('reimagined-demo-pslf-scenario');
  }

  // Reload to re-run the route model hook with new overrides
  const url = new URL(window.location.href);
  if (state.enabled) {
    url.searchParams.set(URL_PARAM, 'true');
  } else {
    url.searchParams.delete(URL_PARAM);
  }
  // Clean up any leftover demo_pslf_scenario URL param
  url.searchParams.delete('demo_pslf_scenario');
  window.location.assign(url.toString());
}

export function clearPslfDebugState(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY);
  // Also clean up legacy demo scenario state
  localStorage.removeItem('reimagined-demo-pslf-scenario');
  const url = new URL(window.location.href);
  url.searchParams.delete(URL_PARAM);
  url.searchParams.delete('debug');
  url.searchParams.delete('demo_pslf_scenario');
  window.location.assign(url.toString());
}

// ---------------------------------------------------------------------------
// Presets
// ---------------------------------------------------------------------------

export const PSLF_DEBUG_PRESETS: Record<string, { label: string; state: Partial<PslfDebugState> }> =
  {
    blankSlate: {
      label: 'Blank Slate',
      state: {
        enabled: true,
        formScenario: 'blank-slate',
      },
    },
    allRecs: {
      label: 'All Recommendations',
      state: {
        enabled: true,
        formScenario: 'submitted',
        hasNsldsData: true,
        debugLoans: [
          {
            name: 'Direct Loan',
            loanType: 'direct',
            currentBalance: 45000,
            qualifyingPayments: 45,
          },
          { name: 'FFEL Loan', loanType: 'ffel', currentBalance: 22000, qualifyingPayments: 30 },
        ],
        estimatorCompleted: true,
        showRecommendations: true,
        consolidateLoans: true,
        missingRepaymentPlan: true,
        changeRepaymentPlan: true,
        linkLoans: true,
        recertifyIncome: true,
      },
    },
    annualRenewal: {
      label: 'Annual Renewal',
      state: {
        enabled: true,
        formScenario: 'submitted',
        annualReminder: true,
        hasNsldsData: true,
        showRecommendations: true,
        recertifyIncome: true,
      },
    },
    withNslds: {
      label: 'With NSLDS',
      state: {
        enabled: true,
        formScenario: 'submitted',
        hasNsldsData: true,
        debugLoans: [
          {
            name: 'Direct Loan',
            loanType: 'direct',
            currentBalance: 45000,
            qualifyingPayments: 72,
          },
          {
            name: 'Direct Loan 2',
            loanType: 'direct',
            currentBalance: 15000,
            qualifyingPayments: 45,
          },
        ],
        showRecommendations: true,
        consolidateLoans: true,
        missingRepaymentPlan: true,
      },
    },
    estimatorDone: {
      label: 'Estimator Completed',
      state: {
        enabled: true,
        formScenario: 'submitted',
        hasNsldsData: true,
        estimatorCompleted: true,
        showRecommendations: true,
        recertifyIncome: true,
      },
    },
    noNslds: {
      label: 'No NSLDS Data',
      state: {
        enabled: true,
        formScenario: 'submitted',
        hasNsldsData: false,
      },
    },
  };

// ---------------------------------------------------------------------------
// Mock data factories — used by the route to override model data
// ---------------------------------------------------------------------------

function makeLoanTypeSummary(overrides: Partial<SummaryByLoanType>): SummaryByLoanType {
  return {
    loanType: 'direct',
    loanIds: ['loan-001'],
    currentBalance: 45000,
    interestRate: 5.5,
    monthlyPayment: 350,
    repaymentPlan: 'ibr',
    repaymentPlans: ['ibr'],
    totalDisbursedAmount: 50000,
    federalLoanTime: '120',
    ...overrides,
  };
}

/** Loan summary where all loans are direct with PSLF-eligible plans (no recs triggered) */
export function mockLoanSummaryClean(): LoanSummary {
  return {
    summaryByLoanType: [makeLoanTypeSummary({})],
    errors: [],
  };
}

/** Has non-direct loans → triggers "consolidate loans" recommendation */
export function mockLoanSummaryNonDirect(): LoanSummary {
  return {
    summaryByLoanType: [
      makeLoanTypeSummary({ loanType: 'direct' }),
      makeLoanTypeSummary({
        loanType: 'ffel',
        loanIds: ['loan-002'],
        currentBalance: 22000,
        repaymentPlan: 'standard',
        repaymentPlans: ['standard'],
      }),
    ],
    errors: [],
  };
}

/** Has a loan with no repayment plan → triggers "provide repayment plan" recommendation */
export function mockLoanSummaryMissingPlan(): LoanSummary {
  return {
    summaryByLoanType: [
      makeLoanTypeSummary({
        repaymentPlan: 'noRepaymentPlan',
        repaymentPlans: ['noRepaymentPlan'],
      }),
    ],
    errors: [],
  };
}

/** All plans are non-PSLF-eligible → triggers "change repayment plan" recommendation */
export function mockLoanSummaryNonEligiblePlan(): LoanSummary {
  return {
    summaryByLoanType: [
      makeLoanTypeSummary({ repaymentPlan: 'standard', repaymentPlans: ['standard'] }),
      makeLoanTypeSummary({
        loanIds: ['loan-003'],
        repaymentPlan: 'graduated',
        repaymentPlans: ['graduated'],
      }),
    ],
    errors: [],
  };
}

/**
 * Build a composite mock LoanSummary driven by the debug state flags.
 * When multiple recommendation flags are on, we pick the most
 * "interesting" loan summary that satisfies all of them.
 */
export function buildMockLoanSummary(debugState: PslfDebugState): LoanSummary {
  // Priority: missingRepaymentPlan (blocks changeRepaymentPlan), consolidate, changeRepaymentPlan
  const summaries: SummaryByLoanType[] = [];

  if (debugState.missingRepaymentPlan) {
    summaries.push(
      makeLoanTypeSummary({
        repaymentPlan: 'noRepaymentPlan',
        repaymentPlans: ['noRepaymentPlan'],
      })
    );
  }

  if (debugState.consolidateLoans) {
    summaries.push(
      makeLoanTypeSummary({
        loanType: 'ffel',
        loanIds: ['loan-debug-ffel'],
        currentBalance: 22000,
        repaymentPlan: debugState.missingRepaymentPlan ? 'noRepaymentPlan' : 'standard',
        repaymentPlans: [debugState.missingRepaymentPlan ? 'noRepaymentPlan' : 'standard'],
      })
    );
  }

  if (debugState.changeRepaymentPlan && !debugState.missingRepaymentPlan) {
    summaries.push(
      makeLoanTypeSummary({
        repaymentPlan: 'standard',
        repaymentPlans: ['standard'],
      })
    );
  }

  // If nothing was added, provide a clean direct loan
  if (summaries.length === 0) {
    summaries.push(makeLoanTypeSummary({}));
  }

  return { summaryByLoanType: summaries, errors: [] };
}

/** Form with employerSignedAt set >11 months ago → triggers annual reminder hero state */
export function mockAnnualReminderForm(): LivePslfForm {
  const elevenMonthsAgo = new Date();
  elevenMonthsAgo.setMonth(elevenMonthsAgo.getMonth() - 11);

  return {
    id: 'debug-form-annual',
    status: 'submitted',
    formType: 'currentEmployer',
    borrowerSignedAt: elevenMonthsAgo.toISOString(),
    employerSignedAt: elevenMonthsAgo.toISOString(),
    submittedAt: elevenMonthsAgo.toISOString(),
    createdAt: elevenMonthsAgo.toISOString(),
    borrowerFields: {},
    employerFields: {},
  };
}

/** Form submitted recently (not triggering annual reminder) */
export function mockRecentForm(): LivePslfForm {
  const twoMonthsAgo = new Date();
  twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);

  return {
    id: 'debug-form-recent',
    status: 'submitted',
    formType: 'currentEmployer',
    borrowerSignedAt: twoMonthsAgo.toISOString(),
    employerSignedAt: twoMonthsAgo.toISOString(),
    submittedAt: twoMonthsAgo.toISOString(),
    createdAt: twoMonthsAgo.toISOString(),
    borrowerFields: {},
    employerFields: {},
  };
}

/**
 * Build the pslfData overrides needed for the requested debug state.
 * Merges with existing pslfData rather than replacing it entirely.
 */
export function buildPslfDataOverrides(
  debugState: PslfDebugState,
  existingData: LivePslfData
): LivePslfData {
  const data = { ...existingData };

  if (debugState.annualReminder) {
    // Ensure there's a form old enough to trigger the annual reminder
    data.forms = [mockAnnualReminderForm()];
    data.currentEmployer = {
      name: data.currentEmployer?.name ?? 'Debug Employer',
      isQualifying: true,
      startDate: '2020-01-15',
    };
    // Need NSLDS accounts so it doesn't fall through to no-nslds state
    if (data.accounts.length === 0) {
      data.accounts = buildDebugAccounts(debugState.debugLoans);
    }
  }

  if (debugState.hasNsldsData) {
    // Provide NSLDS accounts built from the debug loan editor
    data.accounts = buildDebugAccounts(debugState.debugLoans);
  } else if (!debugState.annualReminder) {
    // No NSLDS => clear accounts (unless annual reminder already set them)
    data.accounts = [];
  }

  // Ensure we have forms + show recs section when any recommendation is toggled
  if (debugState.showRecommendations && data.forms.length === 0) {
    data.forms = [mockRecentForm()];
  }

  if (debugState.estimatorCompleted) {
    data.hasCompletedEstimator = true;
    data.estimatorResults = {
      estimatedMonthlyPayment: 285,
      estimatedForgivenessAmount: 62340,
      estimatedForgivenessDate: '2034-06-15',
      repaymentPlan: 'IBR',
    };
  }

  data.isLiveData = false;

  return data;
}

/**
 * Build mock NSLDS account data from the debug loan config.
 * Each loan becomes a LiveLoan under a single LiveAccount.
 */
function buildDebugAccounts(loans: DebugLoan[]) {
  return [
    {
      id: 'debug-nslds-account',
      institutionName: 'MOHELA',
      loans: loans.map((loan, i) => ({
        id: `debug-loan-${i + 1}`,
        name: loan.name,
        balance: loan.currentBalance,
        interestRate: 5.5,
        qualifyingPayments: loan.qualifyingPayments,
        repaymentPlan: 'ibr',
        isPaidOff: false,
        status: 'active',
      })),
    },
  ];
}
